import { useState, useEffect, useCallback, useMemo } from 'react';
import Header from './components/Header';
import { HeaderV3 } from './components/HeaderV3';
import AnalysisForm from './components/AnalysisForm';
import AnalysisResult from './components/AnalysisResult';
import SharedAnalysis from './components/SharedAnalysis';
import ResultPage from './components/ResultPage';
import LandingPage from './components/LandingPage';
import StartupLanding from './components/landings/StartupLanding';
import PersonalLanding from './components/landings/PersonalLanding';
import FinancialLanding from './components/landings/FinancialLanding';
import CareerLanding from './components/landings/CareerLanding';
import JWTDebugPage from './components/JWTDebugPage';
import TermsOfUsePage from './components/TermsOfUsePage';
import PrivacyPolicyPage from './components/PrivacyPolicyPage';
import PublicSharesPage from './components/PublicSharesPage';
import { SimpleToast } from './components/SimpleToast';
import CookieConsent from './components/CookieConsent';
import { ListsProvider } from './contexts/ListsContext';


import { Analysis, AnalysisFormData, AnalysisItem, BackgroundAnalysisOptions, BackgroundAnalysisStatus } from './types';
import { analyzeData } from './services/analysisService';
import { clearSuggestedQuestions, getSuggestedQuestions } from './services/abTestService';
import { ShareService } from './services/shareService';
import { getUserId, incrementAnalysisCounter } from './services/userIdService';
import { storeAnalysisData, storeAnalysisTaskIds, getAnalysisDataByKey } from './services/resultStorageService';
import { llmStatsService } from './services/llmStatsService';
import { analyticsService } from './services/analyticsService';
import { forceReloadLists } from './services/expertValidationService';
import { getShareIdFromUrl, isShareRoute, extractShareIdFromPath, extractShareIdFromHash, getPathWithoutLang, getLangFromPath } from './utils/routeUtils';
import { i18n } from './i18n';

export default function App() {
  const [currentAnalysis, setCurrentAnalysis] = useState<Analysis | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Инициализируем sharedAnalysisId с проверкой URL, чтобы сразу определить маршрут share
  const [sharedAnalysisId, setSharedAnalysisId] = useState<string | null>(() => {
    return getShareIdFromUrl();
  });
  const [resultPageData, setResultPageData] = useState<{ userId: string; analysisNumber: number } | null>(null);
  const [resultPageAnalysis, setResultPageAnalysis] = useState<Analysis | null>(null);
  const [pendingDeletion, setPendingDeletion] = useState<{
    item: AnalysisItem;
    timeoutId: NodeJS.Timeout;
  } | null>(null);
  const [originalAnalysis, setOriginalAnalysis] = useState<Analysis | null>(null);
  // Инициализируем showLanding с проверкой URL (путь без языкового префикса)
  const [showLanding, setShowLanding] = useState(() => {
    if (typeof window !== 'undefined') {
      const pathWithoutLang = getPathWithoutLang(window.location.pathname);
      const hash = window.location.hash;
      if (isShareRoute(pathWithoutLang, hash) || pathWithoutLang === '/analysis') {
        return false;
      }
    }
    return true;
  });
  const [currentLanding, setCurrentLanding] = useState<string>('main');
  const [showJWTDebug, setShowJWTDebug] = useState(false);
  const [showTermsOfUse, setShowTermsOfUse] = useState(false);
  const [showPrivacyPolicy, setShowPrivacyPolicy] = useState(false);
  const [showPublicShares, setShowPublicShares] = useState(false);
  const [showAnalysisForm, setShowAnalysisForm] = useState(() => {
    if (typeof window !== 'undefined') {
      return getPathWithoutLang(window.location.pathname) === '/analysis';
    }
    return false;
  });
  const [prefilledDescription, setPrefilledDescription] = useState<string>('');
  const [prefilledExpert, setPrefilledExpert] = useState<string>('');
  const [prefilledPerson, setPrefilledPerson] = useState<string>('');
  const [prefilledCustomExpert, setPrefilledCustomExpert] = useState<string>('');
  const [prefilledCustomPerson, setPrefilledCustomPerson] = useState<string>('');
  const [prefilledQuestion, setPrefilledQuestion] = useState<string>('');

  const inlineAnalysis = useMemo(() => {
    if (!currentAnalysis) {
      return null;
    }

    if (!resultPageData) {
      return currentAnalysis;
    }

    if (typeof window === 'undefined') {
      return null;
    }

    const storedData = getAnalysisDataByKey(resultPageData.userId, resultPageData.analysisNumber);
    const storedExternalId = storedData?.external_id;

    if (!storedExternalId || !currentAnalysis.external_id) {
      return null;
    }

    return storedExternalId === currentAnalysis.external_id ? currentAnalysis : null;
  }, [currentAnalysis, resultPageData]);
  
  // Состояние для cookie consent (по умолчанию аналитика включена — считаем согласие данным, если не отклонили)
  const [, setHasCookieConsent] = useState<boolean>(() => {
    const consent = localStorage.getItem('cookie-consent');
    return consent !== 'declined';
  });
  
  // Состояние для нового дизайна
  const [theme, setTheme] = useState<'dark' | 'light'>(() => {
    // Инициализируем тему из localStorage или используем 'dark' по умолчанию
    const savedTheme = localStorage.getItem('plinus-theme') as 'dark' | 'light' | null;
    return savedTheme || 'dark';
  });
  const [language, setLanguage] = useState<'ru' | 'en'>(() => {
    // Язык из URL при первом рендере (если есть префикс), иначе из localStorage
    if (typeof window !== 'undefined') {
      const urlLang = getLangFromPath(window.location.pathname);
      if (urlLang) return urlLang;
      const saved = localStorage.getItem('plinus-language') as 'ru' | 'en' | null;
      return saved || 'ru';
    }
    return 'ru';
  });

  // Обработчик изменения темы с сохранением в localStorage
  const handleThemeChange = (newTheme: 'dark' | 'light') => {
    setTheme(newTheme);
    localStorage.setItem('plinus-theme', newTheme);
  };

  // Обработчик изменения языка: localStorage + i18n (переход по URL делает LanguageToggle)
  const handleLanguageChange = (newLanguage: 'ru' | 'en') => {
    setLanguage(newLanguage);
    localStorage.setItem('plinus-language', newLanguage);
    void i18n.changeLanguage(newLanguage);
  };
  
  // Состояние для фонового анализа
  const [backgroundAnalysisStatus, setBackgroundAnalysisStatus] = useState<BackgroundAnalysisStatus | null>(null);
  const [currentAnalysisTaskId, setCurrentAnalysisTaskId] = useState<string | null>(null);
  const [currentAnalysisTaskIds, setCurrentAnalysisTaskIds] = useState<string[]>([]);
  const [, setIsAnalysisCancelled] = useState(false);
  const [abortController, setAbortController] = useState<AbortController | null>(null);

  // Обработчик изменения согласия на cookies
  const handleCookieConsentChange = useCallback(async (consent: boolean) => {
    setHasCookieConsent(consent);
    await analyticsService.updateConsent(consent);
  }, []);

  // Глобальный обработчик кликов для элементов с data-metrika
  useEffect(() => {
    const handleMetrikaClick = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      // Ищем ближайший элемент с атрибутом data-metrika (включая сам элемент)
      const elementWithMetrika = target.closest('[data-metrika]') as HTMLElement | null;
      
      if (elementWithMetrika) {
        const goalName = elementWithMetrika.getAttribute('data-metrika');
        if (goalName) {
          // Отправляем событие в Яндекс.Метрику
          analyticsService.trackEvent(goalName);
        }
      }
    };

    // Добавляем обработчик на document для делегирования событий
    document.addEventListener('click', handleMetrikaClick);
    
    // Очистка при размонтировании
    return () => {
      document.removeEventListener('click', handleMetrikaClick);
    };
  }, []);

  // Обработка URL: языковой префикс /ru/ или /en/, затем маршруты. Для /share/xxx редирект на префикс не делаем — язык из localStorage или браузера.
  useEffect(() => {
    analyticsService.initialize();

    const path = window.location.pathname;
    const hash = window.location.hash;
    const urlLang = getLangFromPath(path);
    const pathWithoutLang = urlLang ? getPathWithoutLang(path) : path;
    const isShare = isShareRoute(pathWithoutLang, hash);

    if (!urlLang) {
      const savedLang = localStorage.getItem('plinus-language') as 'ru' | 'en' | null;
      const browserLang = typeof navigator !== 'undefined' && navigator.language.toLowerCase().startsWith('ru') ? 'ru' : 'en';
      const targetLang = savedLang || browserLang;
      if (!isShare) {
        const pathSuffix = path === '/' ? '' : path;
        window.location.replace(`/${targetLang}${pathSuffix}${window.location.search}${window.location.hash}`);
        return;
      }
      localStorage.setItem('plinus-language', targetLang);
      setLanguage(targetLang);
      void i18n.changeLanguage(targetLang);
    } else {
      localStorage.setItem('plinus-language', urlLang);
      setLanguage(urlLang);
      void i18n.changeLanguage(urlLang);
    }

    const shareIdFromPath = extractShareIdFromPath(pathWithoutLang);
    const shareIdFromHash = extractShareIdFromHash(hash);
    const resultMatch = pathWithoutLang.match(/^\/result\/([a-zA-Z0-9]{10})_(\d+)$/);
    const landingMatch = pathWithoutLang.match(/^\/(startup|personal|financial|career)$/);
    const jwtDebugMatch = pathWithoutLang.match(/^\/jwt-debug$/);
    const termsOfUseMatch = pathWithoutLang.match(/^\/terms-of-use$/);
    const privacyPolicyMatch = pathWithoutLang.match(/^\/privacy-policy$/);
    const publicSharesMatch = pathWithoutLang.match(/^\/public-shares$/);
    const analysisFormMatch = pathWithoutLang.match(/^\/analysis$/);

    if (shareIdFromPath) {
      setSharedAnalysisId(shareIdFromPath);
      setResultPageData(null);
      setShowLanding(false);
      setShowAnalysisForm(false);
      setCurrentLanding('main');
      setShowJWTDebug(false);
    } else if (shareIdFromHash) {
      setSharedAnalysisId(shareIdFromHash);
      setResultPageData(null);
      setShowLanding(false);
      setShowAnalysisForm(false);
      setCurrentLanding('main');
      setShowJWTDebug(false);
      const shareLang = localStorage.getItem('plinus-language') as 'ru' | 'en' | null;
      const browserLang = typeof navigator !== 'undefined' && navigator.language.toLowerCase().startsWith('ru') ? 'ru' : 'en';
      const targetLang = shareLang || browserLang;
      localStorage.setItem('plinus-language', targetLang);
      setLanguage(targetLang);
      void i18n.changeLanguage(targetLang);
      window.history.replaceState({}, '', `/share/${shareIdFromHash}${window.location.search}`);
    } else if (resultMatch) {
      const userId = resultMatch[1];
      const analysisNumber = parseInt(resultMatch[2], 10);
      setSharedAnalysisId(null);
      setResultPageData({ userId, analysisNumber });
      setShowLanding(false);
      setShowAnalysisForm(false);
      setCurrentLanding('main');
      setShowJWTDebug(false);
    } else if (landingMatch) {
      setSharedAnalysisId(null);
      setResultPageData(null);
      setShowLanding(true);
      setCurrentLanding(landingMatch[1]);
      setShowJWTDebug(false);
    } else if (jwtDebugMatch) {
      setSharedAnalysisId(null);
      setResultPageData(null);
      setShowLanding(false);
      setCurrentLanding('main');
      setShowJWTDebug(true);
      setShowTermsOfUse(false);
    } else if (termsOfUseMatch) {
      setSharedAnalysisId(null);
      setResultPageData(null);
      setShowLanding(false);
      setCurrentLanding('main');
      setShowJWTDebug(false);
      setShowTermsOfUse(true);
      setShowPrivacyPolicy(false);
    } else if (privacyPolicyMatch) {
      setSharedAnalysisId(null);
      setResultPageData(null);
      setShowLanding(false);
      setCurrentLanding('main');
      setShowJWTDebug(false);
      setShowTermsOfUse(false);
      setShowPrivacyPolicy(true);
    } else if (publicSharesMatch) {
      setSharedAnalysisId(null);
      setResultPageData(null);
      setShowLanding(false);
      setCurrentLanding('main');
      setShowJWTDebug(false);
      setShowTermsOfUse(false);
      setShowPrivacyPolicy(false);
      setShowPublicShares(true);
      setShowAnalysisForm(false);
    } else if (analysisFormMatch) {
      setSharedAnalysisId(null);
      setResultPageData(null);
      setShowLanding(false);
      setCurrentLanding('main');
      setShowJWTDebug(false);
      setShowTermsOfUse(false);
      setShowPrivacyPolicy(false);
      setShowPublicShares(false);
      setShowAnalysisForm(true);
    } else {
      setSharedAnalysisId(null);
      setResultPageData(null);
      setShowLanding(true);
      setCurrentLanding('main');
      setShowJWTDebug(false);
      setShowTermsOfUse(false);
      setShowPrivacyPolicy(false);
      setShowPublicShares(false);
      setShowAnalysisForm(false);
    }
  }, []);

  // Устанавливаем title страницы для inline анализа (с учётом текущего языка)
  useEffect(() => {
    const siteTitle = i18n.t('common:siteTitle');
    if (inlineAnalysis && !resultPageData) {
      const analysisTitle = inlineAnalysis.seo?.title || inlineAnalysis.title || inlineAnalysis.description || 'Результаты анализа';
      document.title = `${analysisTitle} | ${siteTitle}`;
    } else if (!inlineAnalysis && !resultPageData && !sharedAnalysisId) {
      document.title = siteTitle;
    }
  }, [inlineAnalysis, resultPageData, sharedAnalysisId, language]);

  // Синхронизируем атрибут lang у <html> с текущим языком
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  // Мета-тег description по выбранному языку (landing)
  useEffect(() => {
    const meta = document.querySelector('meta[name="description"]');
    if (meta) {
      meta.setAttribute('content', i18n.t('landing:metaDescription'));
    }
  }, [language]);

  // SEO: hreflang для текущей страницы (альтернативные языковые версии)
  useEffect(() => {
    const path = window.location.pathname;
    const pathWithoutLang = getPathWithoutLang(path);
    const suffix = pathWithoutLang === '/' ? '' : pathWithoutLang;
    const base = typeof window !== 'undefined' ? window.location.origin : '';
    const hrefRu = `${base}/ru${suffix || '/'}`;
    const hrefEn = `${base}/en${suffix || '/'}`;

    const existing = document.head.querySelectorAll('link[rel="alternate"][hreflang]');
    existing.forEach((el) => el.remove());

    const linkRu = document.createElement('link');
    linkRu.rel = 'alternate';
    linkRu.hreflang = 'ru';
    linkRu.href = hrefRu;
    document.head.appendChild(linkRu);

    const linkEn = document.createElement('link');
    linkEn.rel = 'alternate';
    linkEn.hreflang = 'en';
    linkEn.href = hrefEn;
    document.head.appendChild(linkEn);
  }, [language, typeof window !== 'undefined' ? window.location.pathname : '']);

  // Обработчик события popstate для кнопки "Назад" браузера (путь с языковым префиксом)
  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname;
      const pathWithoutLang = getPathWithoutLang(path);
      const shareIdFromPath = extractShareIdFromPath(pathWithoutLang);
      const resultMatch = pathWithoutLang.match(/^\/result\/([a-zA-Z0-9]{10})_(\d+)$/);

      if (shareIdFromPath) {
        setSharedAnalysisId(shareIdFromPath);
        setResultPageData(null);
        setShowLanding(false);
        setShowAnalysisForm(false);
        setCurrentLanding('main');
        setShowJWTDebug(false);
      } else if (resultMatch) {
        const userId = resultMatch[1];
        const analysisNumber = parseInt(resultMatch[2], 10);
        setSharedAnalysisId(null);
        setResultPageData({ userId, analysisNumber });
        setShowLanding(false);
        setShowAnalysisForm(false);
        setCurrentLanding('main');
        setShowJWTDebug(false);
      } else if (pathWithoutLang === '/jwt-debug') {
        setSharedAnalysisId(null);
        setResultPageData(null);
        setShowLanding(false);
        setCurrentLanding('main');
        setShowJWTDebug(true);
        setShowTermsOfUse(false);
      } else if (pathWithoutLang === '/terms-of-use') {
        setSharedAnalysisId(null);
        setResultPageData(null);
        setShowLanding(false);
        setCurrentLanding('main');
        setShowJWTDebug(false);
        setShowTermsOfUse(true);
        setShowPrivacyPolicy(false);
      } else if (pathWithoutLang === '/privacy-policy') {
        setSharedAnalysisId(null);
        setResultPageData(null);
        setShowLanding(false);
        setCurrentLanding('main');
        setShowJWTDebug(false);
        setShowTermsOfUse(false);
        setShowPrivacyPolicy(true);
      } else if (pathWithoutLang === '/public-shares') {
        setSharedAnalysisId(null);
        setResultPageData(null);
        setShowLanding(false);
        setCurrentLanding('main');
        setShowJWTDebug(false);
        setShowTermsOfUse(false);
        setShowPrivacyPolicy(false);
        setShowPublicShares(true);
        setShowAnalysisForm(false);
      } else if (pathWithoutLang === '/analysis') {
        setSharedAnalysisId(null);
        setResultPageData(null);
        setShowLanding(false);
        setCurrentLanding('main');
        setShowJWTDebug(false);
        setShowTermsOfUse(false);
        setShowPrivacyPolicy(false);
        setShowPublicShares(false);
        setShowAnalysisForm(true);
      } else if (pathWithoutLang === '/' || pathWithoutLang === '') {
        setSharedAnalysisId(null);
        setResultPageData(null);
        setShowLanding(true);
        setCurrentLanding('main');
        setShowJWTDebug(false);
        setShowTermsOfUse(false);
        setShowPrivacyPolicy(false);
        setShowPublicShares(false);
        setShowAnalysisForm(false);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const handleBackToAnalysis = () => {
    // Обновляем состояние сначала
    setShowPublicShares(false);
    setSharedAnalysisId(null);
    setResultPageData(null);
    setResultPageAnalysis(null);
    setShowAnalysisForm(false);
    setShowJWTDebug(false);
    setShowTermsOfUse(false);
    setShowPrivacyPolicy(false);
    setCurrentLanding('main');
    setShowLanding(true);
    // Обновляем URL после изменения состояния (с языковым префиксом)
    window.history.pushState({}, '', `/${language}/`);
    window.location.hash = '';
  };

  const handleGoToJWTDebug = () => {
    setSharedAnalysisId(null);
    setResultPageData(null);
    setResultPageAnalysis(null);
    setShowLanding(false);
    setCurrentLanding('main');
    setShowJWTDebug(true);
    setShowTermsOfUse(false);
    setShowPrivacyPolicy(false);
    setShowPublicShares(false);
    window.history.pushState({}, '', `/${language}/jwt-debug`);
  };

  const handleGoToPublicShares = () => {
    setSharedAnalysisId(null);
    setResultPageData(null);
    setResultPageAnalysis(null);
    setShowLanding(false);
    setCurrentLanding('main');
    setShowJWTDebug(false);
    setShowTermsOfUse(false);
    setShowPrivacyPolicy(false);
    setShowPublicShares(true);
    window.history.pushState({}, '', `/${language}/public-shares`);
  };

  const handleLandingChange = (landing: string) => {
    setCurrentLanding(landing);
    // Очищаем ошибку при смене лендинга
    setError(null);
    
    // Отменяем анализ при переходе между лендингами
    if (currentAnalysisTaskId || abortController || backgroundAnalysisStatus) {
      console.log('🔄 Отменяем анализ при переходе между лендингами');
      if (abortController) {
        abortController.abort();
      }
      // Очищаем все состояния анализа
      setBackgroundAnalysisStatus(null);
      setCurrentAnalysisTaskId(null);
      setAbortController(null);
      setIsLoading(false);
    }
    
    if (landing === 'main') {
      setResultPageData(null);
      setResultPageAnalysis(null);
      setShowTermsOfUse(false);
      setShowPrivacyPolicy(false);
      window.history.pushState({}, '', `/${language}/`);
    } else {
      setShowTermsOfUse(false);
      setShowPrivacyPolicy(false);
      window.history.pushState({}, '', `/${language}/${landing}`);
    }
  };

  // Функция для очистки ошибки
  const handleClearError = () => {
    setError(null);
  };

  // Функция для вычисления isModified для элементов
  const calculateIsModified = (currentItems: AnalysisItem[], originalItems: AnalysisItem[]) => {
    return currentItems.map(currentItem => {
      // Если это custom элемент (создан пользователем), он всегда изменен
      if (currentItem.id.toString().startsWith('custom_')) {
        return { ...currentItem, isModified: true };
      }
      
      // Если это расширенный аргумент (добавлен ИИ), он также считается измененным
      if (currentItem.isExpanded) {
        return { ...currentItem, isModified: true };
      }
      
      // Ищем соответствующий элемент в оригинальном анализе
      const originalItem = originalItems.find(item => item.id === currentItem.id);
      
      if (!originalItem) {
        // Элемент не найден в оригинале - это может быть ошибка
        return { ...currentItem, isModified: false };
      }
      
      // Проверяем, изменился ли рейтинг или категория
      const hasChanges = currentItem.rating !== originalItem.rating || currentItem.category !== originalItem.category;
      
      return { ...currentItem, isModified: hasChanges };
    });
  };

  // Функция для вычисления глобального isModified
  const calculateGlobalIsModified = (currentItems: AnalysisItem[], originalItems: AnalysisItem[]) => {
    // Проверяем, есть ли измененные элементы
    const hasModifiedItems = currentItems.some(item => {
      if (item.id.toString().startsWith('custom_')) return true;
      if (item.isExpanded) return true; // Расширенные аргументы считаются измененными
      const originalItem = originalItems.find(oi => oi.id === item.id);
      if (!originalItem) return false;
      return item.rating !== originalItem.rating || item.category !== originalItem.category;
    });
    
    // Проверяем, есть ли удаленные элементы
    const hasDeletedItems = originalItems.some(originalItem => 
      !currentItems.find(currentItem => currentItem.id === originalItem.id)
    );
    
    return hasModifiedItems || hasDeletedItems;
  };

  const handleAnalysisSubmit = async (data: AnalysisFormData, options?: BackgroundAnalysisOptions) => {
    console.log('🔄 handleAnalysisSubmit - Начало');
    console.log('🔄 handleAnalysisSubmit - Полученные данные:', data);
    console.log('🔄 handleAnalysisSubmit - currentAnalysisTaskId:', currentAnalysisTaskId);
    console.log('🔄 handleAnalysisSubmit - abortController:', abortController);
    console.log('🔄 handleAnalysisSubmit - abortController?.signal.aborted:', abortController?.signal.aborted);
    
    // Очищаем массив taskId только при начале нового анализа пользователем (не при автоматическом перезапуске)
    const isAutomaticRestart = data.noQuestion === true && (currentAnalysisTaskId || backgroundAnalysisStatus);
    if (!isAutomaticRestart) {
      console.log('🔄 Очищаем taskIds - это новый анализ пользователем');
      llmStatsService.clearTaskIds();
      setCurrentAnalysisTaskIds([]);
    } else {
      console.log('🔄 Сохраняем существующие taskIds - это автоматический перезапуск');
    }
    
    // Если уже есть активный анализ, отменяем его
    if (currentAnalysisTaskId || abortController || backgroundAnalysisStatus) {
      console.log('🔄 Отменяем предыдущий анализ:', currentAnalysisTaskId);
      if (abortController) {
        console.log('🔄 Вызываем abortController.abort()');
        abortController.abort();
        console.log('🔄 AbortController.aborted после abort():', abortController.signal.aborted);
      } else {
        console.log('🔄 AbortController не найден для отмены');
      }
      
      // Очищаем все состояния предыдущего анализа
      setBackgroundAnalysisStatus(null);
      setCurrentAnalysisTaskId(null);
      setAbortController(null);
      setError(null);
    }
    
    setIsLoading(true);
    setError(null);
    setBackgroundAnalysisStatus(null);
    setCurrentAnalysisTaskId(null);
    setIsAnalysisCancelled(false);
    
    // Сбрасываем состояния шеринга при запуске нового анализа
    setShareUrl(null);
    setShareError(null);
    setIsExportingPDF(false);
    
    // Создаем новый AbortController для этого анализа
    const newAbortController = new AbortController();
    console.log('🆕 Создан новый AbortController:', newAbortController);
    setAbortController(newAbortController);

    try {
      // Добавляем информацию о текущем лендинге
      const dataWithLanding = {
        ...data,
        landing: currentLanding
      };
      
      // Всегда используем фоновый анализ
      const analysisOptions = {
        pollInterval: 5000, // 5 секунд
        onStatusUpdate: (status: BackgroundAnalysisStatus) => {
          setBackgroundAnalysisStatus(status);
          setCurrentAnalysisTaskId(status.taskId);
          
          // Добавляем taskId в массив для статистики LLM
          if (status.taskId) {
            llmStatsService.addTaskId(status.taskId);
            setCurrentAnalysisTaskIds(prev => {
              const newTaskIds = prev.includes(status.taskId) ? prev : [...prev, status.taskId];
              return newTaskIds;
            });
          }
          
          // Если анализ завершился с ошибкой, обрабатываем в зависимости от типа
          if (status.status === 'failed') {
            // Если это validation_error с examples, НЕ очищаем статус - пусть AnalysisForm обработает
            if (status.data?.type === 'validation_error' && status.data?.examples) {
              console.log('🔄 Validation error с примерами - передаем в AnalysisForm');
              // НЕ устанавливаем error и НЕ очищаем backgroundAnalysisStatus
              // AnalysisForm покажет модальное окно
              return;
            }
            
            // Для всех остальных ошибок используем старую логику
            let errorMessage = status.data?.error || 'Анализ завершился с ошибкой';
            
            // Улучшенный парсинг ошибки
            if (typeof errorMessage === 'string') {
              // Сначала проверяем, содержит ли ошибка HTTP статус
              if (errorMessage.includes('HTTP ')) {
                const httpMatch = errorMessage.match(/HTTP \d+: (.+)/);
                if (httpMatch) {
                  const httpError = httpMatch[1];
                  // Пытаемся парсить JSON из HTTP текста
                  try {
                    const httpParsed = JSON.parse(httpError);
                    if (typeof httpParsed === 'object' && httpParsed !== null) {
                      errorMessage = httpParsed.error || httpParsed.message || httpError;
                    } else {
                      errorMessage = httpError;
                    }
                  } catch (e) {
                    // Если не JSON, используем HTTP текст как есть
                    errorMessage = httpError;
                  }
                }
              } else {
                // Если нет HTTP статуса, пытаемся парсить как JSON
                try {
                  const parsedError = JSON.parse(errorMessage);
                  if (typeof parsedError === 'object' && parsedError !== null) {
                    errorMessage = parsedError.error || parsedError.message || errorMessage;
                  }
                } catch (e) {
                  // Если не JSON, используем строку как есть
                }
              }
            }
            
            setError(errorMessage);
            
            // При ошибке отменяем анализ и очищаем все состояния
            console.log('🔄 Отменяем анализ при ошибке');
            newAbortController.abort();
            setBackgroundAnalysisStatus(null);
            setCurrentAnalysisTaskId(null);
            setAbortController(null);
          }
        },
        onProgress: () => {
        },
        onCancelled: () => {
          setIsAnalysisCancelled(true);
        },
        ...options
      };
      
      // НЕ устанавливаем isLoading в false - пусть остается true во время выполнения анализа
      
      const analysis = await analyzeData(
        dataWithLanding, 
        analysisOptions,
        newAbortController.signal
      );
      
      // Проверяем, не является ли это validation_error
      if (analysis && typeof analysis === 'object' && '__validationError' in analysis) {
        console.log('🔄 Получен validation_error, обрабатываем специально');
        // Устанавливаем статус для AnalysisForm
        const validationResult = analysis as any;
        setBackgroundAnalysisStatus({
          taskId: validationResult.taskId,
          status: 'failed',
          data: {
            type: 'validation_error',
            error: validationResult.error,
            examples: validationResult.examples
          }
        } as any);
        return; // Выходим, не продолжая обработку
      }
      
      // Сохраняем оригинальный анализ
      setOriginalAnalysis(JSON.parse(JSON.stringify(analysis)));
      
      // Вычисляем исходный вердикт на основе исходных items от ИИ
      const { calculateVerdict } = await import('./utils/verdictCalculator');
      const originalDecision = calculateVerdict(analysis.items);
      
      // Вычисляем isModified для элементов
      const analysisWithModified = {
        ...analysis,
        items: calculateIsModified(analysis.items, analysis.items),
        isModified: false, // Изначально нет изменений
        originalDecision // Сохраняем исходный вердикт
      };
      
      setCurrentAnalysis(analysisWithModified);
      
      // Отправляем категорию анализа в Яндекс Метрику сразу после завершения анализа
      const eventParams = analysisWithModified.category 
        ? { category: analysisWithModified.category }
        : undefined;
      
      analyticsService.trackEvent('analysis-completed', eventParams);
      
      // Инициализируем состояния шеринга при установке анализа
      setShareUrl(null);
      setShareError(null);
      setHasBeenShared(false);
      setIsExportingPDF(false);
      
      setPrefilledDescription(''); // Очищаем предзаполненное описание после успешного анализа
      setPrefilledExpert(''); // Очищаем предзаполненного эксперта после успешного анализа
      setPrefilledPerson(''); // Очищаем предзаполненную личность после успешного анализа
      setPrefilledCustomExpert(''); // Очищаем предзаполненного custom эксперта после успешного анализа
      setPrefilledCustomPerson(''); // Очищаем предзаполненную custom личность после успешного анализа
      setPrefilledQuestion(''); // Очищаем предзаполненный вопрос после успешного анализа
      // НЕ очищаем предложенные вопросы здесь - они нужны для отображения на странице результатов
      
      // Сохраняем анализ в localStorage и переходим на страницу результата
      if (analysisWithModified.external_id) {
        // Увеличиваем счетчик анализов
        const analysisNumber = incrementAnalysisCounter();
        
        // Сохраняем данные анализа в localStorage
        storeAnalysisData(analysisNumber, analysisWithModified.external_id, analysisWithModified.title);
        
        // Сохраняем массив taskId для статистики LLM
        const taskIds = llmStatsService.getTaskIds();
        if (taskIds.length > 0) {
          storeAnalysisTaskIds(analysisNumber, taskIds);
        }
        
        // Получаем ID пользователя для формирования URL
        const userId = getUserId();
        
        // Формируем URL результата
        const resultUrl = `/result/${userId}_${analysisNumber}`;
        
        // Переходим на страницу результата
        window.history.pushState({}, '', resultUrl);
        setResultPageData({ userId, analysisNumber });
        // Очищаем currentAnalysis, чтобы использовался ResultPage вместо inlineAnalysis
        setCurrentAnalysis(null);
        setShowLanding(false);
        setShowAnalysisForm(false); // Скрываем форму анализа при переходе на результат
        
        console.log('✅ Анализ сохранен и переход на страницу результата:', resultUrl);
      } else {
        // Fallback - если нет external_id, показываем результат как обычно
        setShowLanding(false);
      }
      
      // Очищаем состояние фонового анализа
      setBackgroundAnalysisStatus(null);
      setCurrentAnalysisTaskId(null);
      setAbortController(null);
      
      // Сбрасываем состояние загрузки при успешном завершении
      setIsLoading(false);
      
      // Очищаем ожидающее удаление при новом анализе
      if (pendingDeletion) {
        clearTimeout(pendingDeletion.timeoutId);
        setPendingDeletion(null);
      }
    } catch (error) {
      console.error('Ошибка анализа:', error);
      const errorMessage = error instanceof Error ? error.message : 'Произошла ошибка при анализе';
      
      // Если это ошибка отмены, не показываем её пользователю
      if (errorMessage.includes('Анализ отменен пользователем')) {
        console.log('✅ Анализ был отменен - это нормально');
        return; // Просто выходим, не показывая ошибку
      }
      
      setError(errorMessage);
      
      // Очищаем состояние фонового анализа при ошибке
      setBackgroundAnalysisStatus(null);
      setCurrentAnalysisTaskId(null);
      setAbortController(null);
      
      // При ошибке возвращаемся к лендингу
    } finally {
      setIsLoading(false);
    }
  };

  const handleItemMove = (itemId: string, newCategory: 'plus' | 'minus') => {
    if (!currentAnalysis || !originalAnalysis) return;
    
    // Сбрасываем ссылку для шеринга при изменениях
    setShareUrl(null);
    setShareError(null);
    setIsExportingPDF(false);
    
    setCurrentAnalysis(prev => {
      if (!prev) return prev;
      
      const updatedItems = prev.items.map(item =>
        item.id === itemId ? { ...item, category: newCategory } : item
      );
      
      return {
        ...prev,
        items: calculateIsModified(updatedItems, originalAnalysis.items),
        isModified: calculateGlobalIsModified(updatedItems, originalAnalysis.items),
        originalDecision: prev.originalDecision // Сохраняем originalDecision при обновлении
      };
    });
  };

  const handleRatingChange = (itemId: string, rating: number) => {
    if (!currentAnalysis || !originalAnalysis) return;
    
    // Сбрасываем ссылку для шеринга при изменениях
    setShareUrl(null);
    setShareError(null);
    setIsExportingPDF(false);
    
    setCurrentAnalysis(prev => {
      if (!prev) return prev;
      
      const updatedItems = prev.items.map(item =>
        item.id === itemId ? { ...item, rating } : item
      );
      
      return {
        ...prev,
        items: calculateIsModified(updatedItems, originalAnalysis.items),
        isModified: calculateGlobalIsModified(updatedItems, originalAnalysis.items),
        originalDecision: prev.originalDecision // Сохраняем originalDecision при обновлении
      };
    });
  };

  const handleItemDelete = (itemId: string) => {
    if (!currentAnalysis || !originalAnalysis) return;
    
    // Сбрасываем ссылку для шеринга при изменениях
    setShareUrl(null);
    setShareError(null);
    setIsExportingPDF(false);
    
    // Находим элемент для удаления
    const itemToDelete = currentAnalysis.items.find(item => item.id === itemId);
    if (!itemToDelete) return;
    
    // Если уже есть ожидающее удаление, отменяем его
    if (pendingDeletion) {
      clearTimeout(pendingDeletion.timeoutId);
    }
    
    // Удаляем элемент из анализа
    setCurrentAnalysis(prev => {
      if (!prev) return prev;
      
      const updatedItems = prev.items.filter(item => item.id !== itemId);
      
      return {
        ...prev,
        items: calculateIsModified(updatedItems, originalAnalysis.items),
        isModified: calculateGlobalIsModified(updatedItems, originalAnalysis.items),
        originalDecision: prev.originalDecision // Сохраняем originalDecision при обновлении
      };
    });
    
    // Устанавливаем таймер на 10 секунд для окончательного удаления
    const timeoutId = setTimeout(() => {
      setPendingDeletion(null);
    }, 10000); // 10 секунд
    
    // Сохраняем информацию об ожидающем удалении
    setPendingDeletion({
      item: itemToDelete,
      timeoutId
    });
  };

  const handleUndoDelete = () => {
    if (!pendingDeletion || !currentAnalysis || !originalAnalysis) return;
    
    // Сбрасываем ссылку для шеринга при изменениях
    setShareUrl(null);
    setShareError(null);
    setIsExportingPDF(false);
    
    // Отменяем таймер
    clearTimeout(pendingDeletion.timeoutId);
    
    // Возвращаем элемент в анализ
    setCurrentAnalysis(prev => {
      if (!prev) return prev;
      
      const updatedItems = [...prev.items, pendingDeletion.item];
      
      return {
        ...prev,
        items: calculateIsModified(updatedItems, originalAnalysis.items),
        isModified: calculateGlobalIsModified(updatedItems, originalAnalysis.items),
        originalDecision: prev.originalDecision // Сохраняем originalDecision при обновлении
      };
    });
    
    // Очищаем ожидающее удаление
    setPendingDeletion(null);
  };

  const handleAddItem = (text: string, category: 'plus' | 'minus', rating: number) => {
    if (!currentAnalysis || !originalAnalysis) return;
    
    // Сбрасываем ссылку для шеринга при изменениях
    setShareUrl(null);
    setShareError(null);
    setIsExportingPDF(false);
    
    // Проверяем ограничение на количество custom элементов
    const customItemsCount = currentAnalysis.items.filter(item => 
      item.id.toString().startsWith('custom_')
    ).length;
    
    const MAX_CUSTOM_ITEMS = 50;
    
    if (customItemsCount >= MAX_CUSTOM_ITEMS) {
      alert(`Достигнут лимит custom элементов (${MAX_CUSTOM_ITEMS}). Удалите некоторые элементы перед добавлением новых.`);
      return;
    }
    
    // Генерируем уникальный ID для нового элемента
    const newId = `custom_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    const newItem: AnalysisItem = {
      id: newId,
      text,
      category,
      rating
    };
    
    setCurrentAnalysis(prev => {
      if (!prev) return prev;
      
      const updatedItems = [...prev.items, newItem];
      
      return {
        ...prev,
        items: calculateIsModified(updatedItems, originalAnalysis.items),
        isModified: calculateGlobalIsModified(updatedItems, originalAnalysis.items),
        originalDecision: prev.originalDecision // Сохраняем originalDecision при обновлении
      };
    });
  };

  const handleAnalysisUpdate = (updatedAnalysis: Analysis) => {
    // Сохраняем originalDecision при обновлении, если он не был передан
    setCurrentAnalysis(prev => ({
      ...updatedAnalysis,
      originalDecision: updatedAnalysis.originalDecision || prev?.originalDecision
    }));
    // НЕ обновляем originalAnalysis - он должен содержать исходное состояние
  };

  const handleNewAnalysis = () => {
    // Открываем форму анализа в новой вкладке
    window.open('/analysis', '_blank');
  };

  // Функции для распространения анализа
  const [isSharing, setIsSharing] = useState(false);
  const [shareUrl, setShareUrl] = useState<string | null>(null);
  const [shareId, setShareId] = useState<string | null>(null);
  const [shareError, setShareError] = useState<string | null>(null);
  const [hasBeenShared, setHasBeenShared] = useState(false);
  const [isExportingPDF, setIsExportingPDF] = useState(false);


  const handleShare = async (analysisOverride?: Analysis): Promise<string | null> => {
    // ВАЖНО: Всегда используем resultPageAnalysis, так как он содержит актуальные изменения
    const analysis = analysisOverride || resultPageAnalysis || currentAnalysis;
    if (!analysis) {
      console.error('No analysis available for sharing');
      return null;
    }
    
    setIsSharing(true);
    setShareError(null);
    
    try {
      const response = await ShareService.shareAnalysis(analysis.external_id || analysis.id, analysis.items);
        if (response.share_id) {
          // Используем share_url из ответа API, если он есть (с учетом slug), иначе генерируем URL
          const shareUrl = response.share_url || `${window.location.origin}/share/${response.share_id}`;
          setShareUrl(shareUrl);
          setShareId(response.share_id);
          setHasBeenShared(true);
          
          // НЕ сбрасываем флаги изменений после шеринга!
          // isModified нужны для отображения изменений в UI и логики "Новая ссылка"
          return shareUrl;
        } else {
          setShareError('Ошибка при создании ссылки для распространения');
          return null;
        }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Ошибка при создании ссылки для распространения';
      setShareError(errorMessage);
      return null;
    } finally {
      setIsSharing(false);
    }
  };


  const handlePublicToggle = useCallback((isPublic: boolean) => {
    console.log(`Public flag changed to: ${isPublic}`);
    // Здесь можно добавить дополнительную логику при изменении флага
    // Например, обновление UI или отправка аналитики
  }, []);

  const handleExportPDF = useCallback(async (analysisOverride?: Analysis) => {
    if (isExportingPDF) {
      return;
    }
    
    // Если нет shareUrl, создаем его сначала
    let currentShareUrl = shareUrl;
    if (!currentShareUrl) {
      currentShareUrl = await handleShare(analysisOverride);
      if (!currentShareUrl) {
        console.error('Failed to create share for PDF export');
        return;
      }
    }
    setIsExportingPDF(true);
    setShareError(null);

    try {
      // Извлекаем shareId из URL
      const shareId = currentShareUrl.split('/share/')[1];
      if (!shareId) {
        console.error('Не удалось извлечь shareId из URL');
        setShareError('Ошибка при экспорте PDF');
        setIsExportingPDF(false);
        return;
      }

      const SHARE_SERVER_URL = import.meta.env.VITE_SHARE_SERVER_URL || 'http://localhost:3002';
      const pdfUrl = `${SHARE_SERVER_URL}/api/share/${shareId}/pdf`;
      
      // Проверяем доступность URL перед скачиванием
      const response = await fetch(pdfUrl, { method: 'HEAD' });
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      // Создаем временную ссылку для скачивания
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = `analysis-share-${shareId.substring(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Сбрасываем состояние через 2 секунды после успешного скачивания
      setTimeout(() => {
        setIsExportingPDF(false);
      }, 2000);
      
    } catch (error) {
      console.error('Error exporting PDF:', error);
      const errorMessage = error instanceof Error ? error.message : 'Ошибка при экспорте PDF';
      setShareError(errorMessage);
      setIsExportingPDF(false);
    }
  }, [shareUrl, isExportingPDF]);


  const handleGoToForm = () => {
    // Открываем форму анализа в новой вкладке
    window.open('/analysis', '_blank');
  };

  const handleCancelAnalysis = () => {
    console.log('🔄 Отменяем анализ при вводе пользователя');
    if (abortController) {
      abortController.abort();
    }
    // Полностью очищаем все состояния фонового анализа
    setCurrentAnalysisTaskId(null);
    setBackgroundAnalysisStatus(null);
    setAbortController(null);
    setIsLoading(false);
    setError(null);
    
    // Очищаем предзаполненные значения при отмене анализа
    setPrefilledDescription('');
    setPrefilledExpert('');
    setPrefilledPerson('');
    setPrefilledCustomExpert('');
    setPrefilledCustomPerson('');
    setPrefilledQuestion('');
    clearSuggestedQuestions(); // Очищаем предложенные вопросы
    setShowTermsOfUse(false);
    setShowPrivacyPolicy(false);
  };

  const handleClearValidationError = () => {
    console.log('🔄 Очищаем validation error статус');
    setBackgroundAnalysisStatus(null);
    setCurrentAnalysisTaskId(null);
    setAbortController(null);
  };

  const handleUseImprovedTemplate = async (template: string, expert?: string, person?: string, customExpert?: string, customPerson?: string, promptPackage?: string) => {
    try {
      console.log('🔍 App - Обработка шаблона:', {
        expert,
        person,
        customExpert,
        customPerson,
        promptPackage
      });
      
      // Загружаем актуальные списки экспертов и личностей для текущего языка
      const { experts, persons } = await forceReloadLists(i18n.language);
      
      console.log('🔍 App - Загружены списки:', {
        expertsCount: experts.length,
        personsCount: persons.length
      });
      
      let finalExpert: string = '';
      let finalPerson: string = '';
      let finalCustomExpert = '';
      let finalCustomPerson = '';
      
      // Используем promptPackage для определения типа выбора
      const promptPackageValue = promptPackage?.trim().toLowerCase();
      const isExpertPackage = promptPackageValue === 'expert';
      const isPersonPackage = promptPackageValue === 'person';
      
      console.log('🔍 App - promptPackage проверка:', {
        original: promptPackage,
        trimmed: promptPackageValue,
        isExpertPackage,
        isPersonPackage
      });
      
      // Обрабатываем expert только если он явно указан
      if (expert !== undefined && expert !== null && expert !== '') {
        if (expert === 'custom') {
          // Если expert = 'custom', то это custom эксперт
          finalExpert = 'custom';
          finalCustomExpert = customExpert || '';
        } else {
          // Проверяем, есть ли эксперт в списке экспертов (по value или label)
          const matchedExpert = experts.find(e => 
            e.value === expert || 
            e.label.toLowerCase() === expert.toLowerCase()
          );
          
          if (matchedExpert) {
            // Это эксперт из списка - используем его value
            finalExpert = matchedExpert.value;
          } else {
            // Не найден в списке экспертов
            // Если promptPackage = 'expert', то это точно эксперт (custom), не проверяем на личность
            if (isExpertPackage) {
              // Это custom эксперт (promptPackage = 'expert')
              finalExpert = 'custom';
              finalCustomExpert = expert;
              console.log('🔍 App - Эксперт не найден в списке, но promptPackage=expert, считаем custom:', expert);
            } else {
              // Проверяем, может это личность (только если promptPackage не 'expert')
              const matchedPerson = persons.find(p => 
                p.value === expert || 
                p.label.toLowerCase() === expert.toLowerCase()
              );
              
              if (matchedPerson) {
                // Это личность, а не эксперт - не устанавливаем expert
                finalExpert = '';
                // Устанавливаем person только если он еще не установлен
                if (!finalPerson) {
                  finalPerson = matchedPerson.value;
                }
              } else {
                // Это custom эксперт
                finalExpert = 'custom';
                finalCustomExpert = expert;
              }
            }
          }
        }
      }
      
      // Обрабатываем person только если он явно указан (и не был уже установлен из expert)
      if (person !== undefined && person !== null && person !== '' && !finalPerson) {
        if (person === 'custom') {
          // Если person = 'custom', то это custom личность
          finalPerson = 'custom';
          finalCustomPerson = customPerson || '';
        } else {
          // Проверяем, есть ли личность в списке личностей (по value или label)
          const matchedPerson = persons.find(p => 
            p.value === person || 
            p.label.toLowerCase() === person.toLowerCase()
          );
          
          if (matchedPerson) {
            // Это личность из списка - используем ее value
            finalPerson = matchedPerson.value;
          } else {
            // Не найдена в списке - это custom личность
            finalPerson = 'custom';
            finalCustomPerson = person;
          }
        }
      }
      
      console.log('🔍 App - Финальные значения для формы:', {
        finalExpert,
        finalPerson,
        finalCustomExpert,
        finalCustomPerson
      });
      
      // Устанавливаем предзаполненные значения из шаблона
      setPrefilledDescription(template);
      setPrefilledExpert(finalExpert);
      setPrefilledPerson(finalPerson);
      setPrefilledCustomExpert(finalCustomExpert);
      setPrefilledCustomPerson(finalCustomPerson);
      setPrefilledQuestion('');
    } catch (error) {
      console.error('❌ Ошибка при обработке шаблона:', error);
      // В случае ошибки просто передаем значения как есть
      setPrefilledDescription(template);
      setPrefilledExpert(expert || '');
      setPrefilledPerson(person || '');
      setPrefilledCustomExpert(customExpert || '');
      setPrefilledCustomPerson(customPerson || '');
      setPrefilledQuestion('');
    }
    
    // Переходим на форму анализа
    setShowLanding(false);
    setShowTermsOfUse(false);
    setShowPrivacyPolicy(false);
    setShowPublicShares(false);
    setShowAnalysisForm(true);
    setCurrentAnalysis(null);
    setOriginalAnalysis(null);
    setError(null);
    
    // Очищаем ожидающее удаление
    if (pendingDeletion) {
      clearTimeout(pendingDeletion.timeoutId);
      setPendingDeletion(null);
    }
    
    // Очищаем sharedAnalysisId и resultPageData чтобы показать форму
    setSharedAnalysisId(null);
    setResultPageData(null);
    setResultPageAnalysis(null);
    
    // Меняем URL на страницу анализа (с языковым префиксом)
    window.history.pushState({}, '', `/${language}/analysis`);
    
    // Отменяем текущий анализ, если он есть
    if (currentAnalysisTaskId || abortController || backgroundAnalysisStatus) {
      if (abortController) {
        abortController.abort();
      }
      setBackgroundAnalysisStatus(null);
      setCurrentAnalysisTaskId(null);
      setAbortController(null);
      setIsLoading(false);
    }
  };

  const handleRestartWithQuestion = (question: string, expert?: string, person?: string, customExpert?: string, customPerson?: string) => {
    // Определяем, является ли expert custom экспертом
    
    let finalExpert = expert || '';
    let finalPerson = person || '';
    let finalCustomExpert = '';
    let finalCustomPerson = '';
    
    // Обрабатываем эксперта
    if (expert === 'custom') {
      finalExpert = 'custom';
      finalCustomExpert = customExpert || '';
    } else if (expert && expert.trim()) {
      // Если эксперт не 'custom', то это либо известный эксперт, либо custom значение
      // Проверяем, есть ли customExpert - если есть, то это custom эксперт
      if (customExpert && customExpert.trim()) {
        finalExpert = 'custom';
        finalCustomExpert = customExpert;
      } else {
        // Это известный эксперт
        finalExpert = expert;
      }
    }
    
    // Обрабатываем личность
    if (person === 'custom') {
      finalPerson = 'custom';
      finalCustomPerson = customPerson || '';
    } else if (person && person.trim()) {
      // Если личность не 'custom', то это либо известная личность, либо custom значение
      // Проверяем, есть ли customPerson - если есть, то это custom личность
      if (customPerson && customPerson.trim()) {
        finalPerson = 'custom';
        finalCustomPerson = customPerson;
      } else {
        // Это известная личность
        finalPerson = person;
      }
    }
    
    // Получаем исходный запрос из suggestedQuestions, если он есть
    const suggestedQuestions = getSuggestedQuestions();
    const originalRequest = suggestedQuestions?.originalRequest || currentAnalysis?.description || '';
    
    // Устанавливаем предзаполненные значения
    setPrefilledDescription(originalRequest);
    setPrefilledExpert(finalExpert);
    setPrefilledPerson(finalPerson);
    setPrefilledCustomExpert(finalCustomExpert);
    setPrefilledCustomPerson(finalCustomPerson);
    setPrefilledQuestion(question);
    
    // Переходим на форму анализа
    setShowLanding(false);
    setShowTermsOfUse(false);
    setShowPrivacyPolicy(false);
    setCurrentAnalysis(null);
    setOriginalAnalysis(null);
    setError(null);
    
    // Очищаем состояния страницы результата чтобы показать форму
    setResultPageData(null);
    setResultPageAnalysis(null);
    
    // Очищаем ожидающее удаление
    if (pendingDeletion) {
      clearTimeout(pendingDeletion.timeoutId);
      setPendingDeletion(null);
    }
    
    // Отменяем текущий анализ, если он есть
    if (currentAnalysisTaskId || abortController || backgroundAnalysisStatus) {
      if (abortController) {
        abortController.abort();
      }
      setBackgroundAnalysisStatus(null);
      setCurrentAnalysisTaskId(null);
      setAbortController(null);
      setIsLoading(false);
    }
    
    // Запускаем новый анализ с вопросом
    setTimeout(() => {
      // Устанавливаем состояние загрузки перед запуском анализа
      setIsLoading(true);
      
      // Определяем финальные значения для отправки
      let submitExpert = finalExpert;
      let submitPerson = finalPerson;
      
      // Если expert = 'custom', используем customExpert
      if (finalExpert === 'custom' && finalCustomExpert) {
        submitExpert = finalCustomExpert;
      }
      
      // Если person = 'custom', используем customPerson
      if (finalPerson === 'custom' && finalCustomPerson) {
        submitPerson = finalCustomPerson;
      }
      
      handleAnalysisSubmit({
        description: originalRequest,
        expert: submitExpert,
        person: submitPerson,
        question: question,
        noQuestion: false
      });
    }, 100);
  };


  // Для share маршрута сразу рендерим SharedAnalysis, минуя главную страницу
  // Проверяем URL напрямую, чтобы избежать проблем с асинхронной инициализацией состояния
  // Но также проверяем состояние sharedAnalysisId, чтобы правильно обрабатывать переходы
  let currentShareId: string | null = sharedAnalysisId;
  if (typeof window !== 'undefined') {
    const shareIdFromUrl = getShareIdFromUrl();
    if (shareIdFromUrl) {
      currentShareId = shareIdFromUrl;
    }
  }

  // Если это share маршрут И sharedAnalysisId установлен (не был очищен), сразу рендерим SharedAnalysis с Header
  // Если sharedAnalysisId был очищен (например, после handleUseImprovedTemplate), не используем ранний return
  if (currentShareId && sharedAnalysisId) {
    return (
      <ListsProvider>
        <div 
          className={`min-h-screen ${
            theme === 'dark' 
              ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900' 
              : 'bg-gradient-to-br from-gray-50 via-white to-gray-100'
          }`}
        >
          <HeaderV3
            language={language}
            onLanguageChange={handleLanguageChange}
            theme={theme}
            onThemeChange={handleThemeChange}
            analysisTitle={currentShareId}
            onGoHome={handleBackToAnalysis}
            onNewAnalysis={() => {
              setSharedAnalysisId(null);
              setResultPageData(null);
              handleNewAnalysis();
              window.history.pushState({}, '', `/${language}/`);
            }}
            // Для share страниц отключаем функциональность шеринга
            analysis={undefined}
            onShare={undefined}
            isSharing={false}
            shareUrl={null}
            hasBeenShared={false}
            onExportPDF={undefined}
            isExportingPDF={false}
            shareError={null}
            shareId={undefined}
            onPublicToggle={undefined}
          />
          <main className="container mx-auto px-4 md:px-6 py-2">
            <SharedAnalysis 
              shareId={currentShareId} 
              onBack={handleBackToAnalysis}
              onUseImprovedTemplate={handleUseImprovedTemplate}
              theme={theme}
              language={language}
            />
          </main>
        </div>
      </ListsProvider>
    );
  }

  const hasActiveAnalysisView = Boolean(inlineAnalysis || resultPageData);

  return (
    <ListsProvider>
      <div className={`min-h-screen ${
        // Для лендинга всегда светлый фон; для соглашения и политики — по выбранной теме
        showLanding
          ? 'bg-gradient-to-br from-gray-50 via-white to-gray-100'
          : theme === 'dark'
            ? 'bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900'
            : 'bg-gradient-to-br from-gray-50 via-white to-gray-100'
      }`}>
        {/* AnimatedGradientBackground убран - GradientBackground достаточно */}
        {/* <AnimatedGradientBackground colors={backgroundColors} duration={15000} /> */}
        

        
        {/* Используем HeaderV3 для страниц с результатами, обычный Header для лендинга */}
        {(sharedAnalysisId || resultPageData) ? (
          <HeaderV3
            language={language}
            onLanguageChange={handleLanguageChange}
            theme={theme}
            onThemeChange={handleThemeChange}
            analysisTitle={resultPageAnalysis?.description || inlineAnalysis?.description || sharedAnalysisId || undefined}
            onGoHome={handleBackToAnalysis}
            onNewAnalysis={() => {
              setSharedAnalysisId(null);
              setResultPageData(null);
              handleNewAnalysis();
              window.history.pushState({}, '', `/${language}/`);
            }}
            // Новые пропсы для шеринга - только для нерасшаренных страниц
            analysis={sharedAnalysisId ? undefined : (resultPageAnalysis || inlineAnalysis || undefined)}
            onShare={sharedAnalysisId ? undefined : handleShare}
            isSharing={sharedAnalysisId ? false : isSharing}
            shareUrl={sharedAnalysisId ? null : shareUrl}
            hasBeenShared={sharedAnalysisId ? false : hasBeenShared}
            onExportPDF={sharedAnalysisId ? undefined : handleExportPDF}
            isExportingPDF={sharedAnalysisId ? false : isExportingPDF}
            shareError={sharedAnalysisId ? null : shareError}
            shareId={sharedAnalysisId ? undefined : shareId || undefined}
            onPublicToggle={sharedAnalysisId ? undefined : handlePublicToggle}
          />
        ) : (
          <Header 
            onGoHome={sharedAnalysisId || resultPageData || showTermsOfUse || showPrivacyPolicy ? handleBackToAnalysis : () => {
              setShowLanding(true);
              setCurrentLanding('main');
              setShowJWTDebug(false);
              window.history.pushState({}, '', `/${language}/`);
            }} 
            showHomeButton={!showLanding && !showJWTDebug}
            onGoToJWTDebug={handleGoToJWTDebug}
            currentLanding={showLanding ? currentLanding : undefined}
            onLandingChange={showLanding ? handleLandingChange : undefined}
            theme={theme}
            isLanding={showLanding}
            language={language}
            onLanguageChange={handleLanguageChange}
          />
        )}
      
      <main className="container mx-auto px-4 md:px-6 py-2">
        {sharedAnalysisId ? (
          <SharedAnalysis 
            shareId={sharedAnalysisId} 
            onBack={handleBackToAnalysis}
            onUseImprovedTemplate={handleUseImprovedTemplate}
            theme={theme}
            language={language}
          />
        ) : showJWTDebug ? (
          import.meta.env.VITE_JWT_DEBUG_ENABLED ? (
            <JWTDebugPage />
          ) : (
            <div className="max-w-4xl mx-auto px-4 py-4 md:py-6">
              <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                <h1 className="text-xl font-semibold text-red-800 mb-2">Доступ запрещен</h1>
                <p className="text-red-700">JWT отладка отключена.</p>
                <p className="text-red-600 text-sm mt-2">
                  Установите переменную окружения JWT_DEBUG_ENABLED=true для доступа к этой странице.
                </p>
              </div>
            </div>
          )
        ) : showTermsOfUse ? (
          <TermsOfUsePage 
            theme={theme}
            onGoHome={handleBackToAnalysis}
          />
        ) : showPrivacyPolicy ? (
          <PrivacyPolicyPage 
            theme={theme}
            onGoHome={handleBackToAnalysis}
          />
        ) : showPublicShares && getPathWithoutLang(window.location.pathname) === '/public-shares' ? (
          <PublicSharesPage 
            onBack={handleBackToAnalysis}
            theme={theme}
            language={language}
          />
        ) : showLanding || getPathWithoutLang(window.location.pathname) === '/' ? (
          <>
            {/* Навигация по лендингам - скрыта на мобильных и десктопе */}
            <div className="hidden mt-8 mb-12">
              {/* Горизонтальный скролл */}
              <div className="flex overflow-x-auto gap-3 sm:gap-4 lg:gap-6 px-6 scrollbar-hide scroll-smooth touch-pan-x">
                <div className="flex gap-3 sm:gap-4 lg:gap-6 flex-shrink-0 min-w-max">
                    <button
                      onClick={() => handleLandingChange('main')}
                      className={`px-4 sm:px-5 py-2.5 rounded-lg font-medium transition-colors text-sm sm:text-base flex-shrink-0 whitespace-nowrap ${
                        currentLanding === 'main' 
                          ? 'bg-blue-600 text-white shadow-md' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Основной
                    </button>
                    <button
                      onClick={() => handleLandingChange('startup')}
                      className={`px-4 sm:px-5 py-2.5 rounded-lg font-medium transition-colors text-sm sm:text-base flex-shrink-0 whitespace-nowrap ${
                        currentLanding === 'startup' 
                          ? 'bg-green-600 text-white shadow-md' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Стартапы
                    </button>
                    <button
                      onClick={() => handleLandingChange('personal')}
                      className={`px-4 sm:px-5 py-2.5 rounded-lg font-medium transition-colors text-sm sm:text-base flex-shrink-0 whitespace-nowrap ${
                        currentLanding === 'personal' 
                          ? 'bg-pink-600 text-white shadow-md' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Личные решения
                    </button>
                    <button
                      onClick={() => handleLandingChange('financial')}
                      className={`px-4 sm:px-5 py-2.5 rounded-lg font-medium transition-colors text-sm sm:text-base flex-shrink-0 whitespace-nowrap ${
                        currentLanding === 'financial' 
                          ? 'bg-yellow-600 text-white shadow-md' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Финансы
                    </button>
                    <button
                      onClick={() => handleLandingChange('career')}
                      className={`px-4 sm:px-5 py-2.5 rounded-lg font-medium transition-colors text-sm sm:text-base flex-shrink-0 whitespace-nowrap ${
                        currentLanding === 'career' 
                          ? 'bg-indigo-600 text-white shadow-md' 
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      Карьера
                    </button>
                  </div>
                </div>
            </div>


            {/* Отображение соответствующего лендинга */}
            {currentLanding === 'main' && (
              <LandingPage 
                onStartAnalysis={(description, expert, person, question, noQuestion) => {
                  if (description) {
                    setPrefilledDescription(description);
                    handleAnalysisSubmit({ description, url: '', expert, person, question, noQuestion });
                  }
                }}
                onCancel={handleCancelAnalysis}
                onGoToForm={handleGoToForm}
                onAbortAnalysis={handleCancelAnalysis}
                isLoading={isLoading}
                backgroundAnalysisStatus={backgroundAnalysisStatus}
                error={error}
                onClearError={handleClearError}
                onClearValidationError={handleClearValidationError}
                onGoToPublicShares={handleGoToPublicShares}
                theme={theme}
              />
            )}
            {currentLanding === 'startup' && (
              <StartupLanding 
                onStartAnalysis={(description, expert, question, noQuestion) => {
                  if (description) {
                    setPrefilledDescription(description);
                    handleAnalysisSubmit({ description, url: '', expert, question, noQuestion });
                  }
                }}
                onCancel={handleCancelAnalysis}
                onGoToForm={handleGoToForm}
                onAbortAnalysis={handleCancelAnalysis}
                isLoading={isLoading}
                backgroundAnalysisStatus={backgroundAnalysisStatus}
                error={error}
                onClearError={handleClearError}
                onClearValidationError={handleClearValidationError}
              />
            )}
            {currentLanding === 'personal' && (
              <PersonalLanding 
                onStartAnalysis={(description, expert, question, noQuestion) => {
                  if (description) {
                    setPrefilledDescription(description);
                    handleAnalysisSubmit({ description, url: '', expert, question, noQuestion });
                  }
                }}
                onCancel={handleCancelAnalysis}
                onGoToForm={handleGoToForm}
                onAbortAnalysis={handleCancelAnalysis}
                isLoading={isLoading}
                backgroundAnalysisStatus={backgroundAnalysisStatus}
                error={error}
                onClearError={handleClearError}
                onClearValidationError={handleClearValidationError}
              />
            )}
            {currentLanding === 'financial' && (
              <FinancialLanding 
                onStartAnalysis={(description, expert, question, noQuestion) => {
                  if (description) {
                    setPrefilledDescription(description);
                    handleAnalysisSubmit({ description, url: '', expert, question, noQuestion });
                  }
                }}
                onCancel={handleCancelAnalysis}
                onGoToForm={handleGoToForm}
                onAbortAnalysis={handleCancelAnalysis}
                isLoading={isLoading}
                backgroundAnalysisStatus={backgroundAnalysisStatus}
                error={error}
                onClearError={handleClearError}
                onClearValidationError={handleClearValidationError}
              />
            )}
            {currentLanding === 'career' && (
              <CareerLanding 
                onStartAnalysis={(description, expert, question, noQuestion) => {
                  if (description) {
                    setPrefilledDescription(description);
                    handleAnalysisSubmit({ description, url: '', expert, question, noQuestion });
                  }
                }}
                onCancel={handleCancelAnalysis}
                onGoToForm={handleGoToForm}
                onAbortAnalysis={handleCancelAnalysis}
                isLoading={isLoading}
                backgroundAnalysisStatus={backgroundAnalysisStatus}
                error={error}
                onClearError={handleClearError}
                onClearValidationError={handleClearValidationError}
              />
            )}
          </>
        ) : showAnalysisForm ? (
          <div className="max-w-2xl mx-auto">
            <AnalysisForm 
              onSubmit={handleAnalysisSubmit}
              onAbortAnalysis={handleCancelAnalysis}
              isLoading={isLoading}
              prefilledDescription={prefilledDescription}
              prefilledExpert={prefilledExpert}
              prefilledPerson={prefilledPerson}
              prefilledCustomExpert={prefilledCustomExpert}
              prefilledCustomPerson={prefilledCustomPerson}
              prefilledQuestion={prefilledQuestion}
              backgroundAnalysisStatus={backgroundAnalysisStatus}
              onCancel={handleCancelAnalysis}
              error={error}
              onClearValidationError={handleClearValidationError}
              theme={theme}
            />
          </div>
        ) : (
          <div>
            {hasActiveAnalysisView ? (
              <>
                {inlineAnalysis ? (
                  <AnalysisResult
                    analysis={inlineAnalysis}
                    onItemMove={handleItemMove}
                    onRatingChange={handleRatingChange}
                    onItemDelete={handleItemDelete}
                    onAddItem={handleAddItem}
                    onAnalysisUpdate={handleAnalysisUpdate}
                    onUseImprovedTemplate={handleUseImprovedTemplate}
                    onRestartWithQuestion={handleRestartWithQuestion}
                    currentTaskIds={currentAnalysisTaskIds}
                    theme={theme}
                    language={language}
                    // Пропсы для шеринга
                    onShare={handleShare}
                    onExportPDF={handleExportPDF}
                    isSharing={isSharing}
                    isExportingPDF={isExportingPDF}
                    shareUrl={shareUrl}
                    hasBeenShared={hasBeenShared}
                    shareError={shareError}
                    shareId={shareId || undefined}
                  />
        ) : resultPageData ? (
          <ResultPage 
            key={`${resultPageData.userId}_${resultPageData.analysisNumber}`}
            userId={resultPageData.userId}
                analysisNumber={resultPageData.analysisNumber}
            onGoHome={handleBackToAnalysis}
            onNewAnalysis={handleNewAnalysis}
            theme={theme}
            language={language}
            onAnalysisLoaded={(analysis) => {
              setResultPageAnalysis(analysis);
              // Сохраняем оригинальный анализ для сравнения
              setOriginalAnalysis(JSON.parse(JSON.stringify(analysis)));
              // Инициализируем состояния шеринга при загрузке анализа
              setShareUrl(null);
              setShareError(null);
              setHasBeenShared(false);
              setIsExportingPDF(false);
            }}
            // Пропсы для шеринга
            onShare={handleShare}
            onExportPDF={handleExportPDF}
            isSharing={isSharing}
            isExportingPDF={isExportingPDF}
            shareUrl={shareUrl}
            hasBeenShared={hasBeenShared}
            shareError={shareError}
            // Пропс для нового вопроса
            onNewQuestion={handleNewAnalysis}
            // Пропс для shareId
            shareId={shareId || undefined}
            onError={() => {
            }}
            onAnalysisUpdate={(updatedAnalysis) => {
              setResultPageAnalysis(updatedAnalysis);
              // Сбрасываем ссылку для шеринга только при новых изменениях после шеринга
              if (hasBeenShared && shareUrl) {
                setShareUrl(null);
                setShareError(null);
                setIsExportingPDF(false);
              }
            }}
            onUseImprovedTemplate={handleUseImprovedTemplate}
            onRestartWithQuestion={handleRestartWithQuestion}
            originalAnalysis={originalAnalysis}
          />
        ) : null}
              </>
            ) : (
              <div className="max-w-2xl mx-auto">
                <div className="text-center mb-6 sm:mb-8">
                  <h1 className={`text-2xl sm:text-3xl font-bold mb-3 sm:mb-4 ${
                    theme === 'dark' ? 'text-white' : 'text-gray-900'
                  }`}>
                    Анализируйте решения с ИИ
                  </h1>
                  <p className={`text-base sm:text-lg px-2 sm:px-0 ${
                    theme === 'dark' ? 'text-slate-300' : 'text-gray-600'
                  }`}>
                    Опишите вашу задачу, и наш ИИ поможет выявить все плюсы и минусы, 
                    оценить их важность и дать рекомендацию
                  </p>
                </div>
                
                <AnalysisForm 
                  onSubmit={handleAnalysisSubmit}
                  onAbortAnalysis={handleCancelAnalysis}
                  isLoading={isLoading}
                  prefilledDescription={prefilledDescription}
                  prefilledExpert={prefilledExpert}
                  prefilledPerson={prefilledPerson}
                  prefilledCustomExpert={prefilledCustomExpert}
                  prefilledCustomPerson={prefilledCustomPerson}
                  prefilledQuestion={prefilledQuestion}
                  backgroundAnalysisStatus={backgroundAnalysisStatus}
                  onCancel={handleCancelAnalysis}
                  error={error}
                  onClearValidationError={handleClearValidationError}
                />
                
                <div className="mt-6 sm:mt-8 grid grid-cols-1 xs:grid-cols-3 gap-3 sm:gap-4">
                  <div className="card text-center p-4 sm:p-6">
                    <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Путешествия</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Анализ поездок, отелей, маршрутов</p>
                  </div>
                  <div className="card text-center p-4 sm:p-6">
                    <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Покупки</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Оценка товаров и услуг</p>
                  </div>
                  <div className="card text-center p-4 sm:p-6">
                    <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Решения</h3>
                    <p className="text-xs sm:text-sm text-gray-600">Любые жизненные выборы</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </main>
      
      {/* Всплывающий банер отмены удаления */}
      {pendingDeletion && (
        <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50 animate-slide-up">
          <div className="bg-blue-600 text-white px-4 sm:px-6 py-3 sm:py-4 rounded-lg shadow-xl border border-blue-500 max-w-xs sm:max-w-sm mx-auto">
            <div className="flex items-start space-x-3">
              <svg className="h-4 w-4 sm:h-5 sm:w-5 text-blue-200 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-white truncate">
                  Элемент "{pendingDeletion.item.text}" удален
                </p>
                <p className="text-xs text-blue-200 mt-1">
                  У вас есть 10 секунд, чтобы 
                  <button
                    onClick={handleUndoDelete}
                    className="ml-1 underline text-blue-100 hover:text-white transition-colors focus:outline-none focus:underline"
                  >
                    отменить
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}


      </div>
      
      {/* Toast notifications */}
      <SimpleToast theme={theme} />
      
      {/* Cookie consent banner */}
      <CookieConsent onConsentChange={handleCookieConsentChange} />
    </ListsProvider>
  );
}